/**
 * Single-flight JWT refresh shared by every axios instance and browser tab.
 * Rotation blacklists the previous refresh, so a second in-flight refresh must
 * not clear the tokens the first one just stored.
 */
import axios from 'axios';

export const ACCESS_KEY = 'accessToken';
export const REFRESH_KEY = 'refreshToken';
export const USER_KEY = 'user';
export const AUTH_TIMEOUT_MS = 15000;

const REFRESH_LOCK = 'tcp-jwt-refresh';

/** @type {Promise<{ status: 'ok', access: string, refresh: string | null } | { status: 'transient' } | { status: 'auth' }> | null} */
let refreshPromise = null;

function apiBase() {
  return (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '');
}

export function refreshUrl() {
  return `${apiBase()}/api/user/token/refresh/`;
}

export function requestUrl(config) {
  if (!config) return '';
  return `${config.baseURL || ''}${config.url || ''}`;
}

export function isRefreshRequest(config) {
  return /\/api\/user\/token\/refresh\/?$/i.test(requestUrl(config)) || /token\/refresh\/?$/i.test(requestUrl(config));
}

/** Login and signup must not send a stale Bearer: the API rejects it before checking the password. */
export function isCredentialRequest(config) {
  return /\/api\/user\/(?:login|signup)\/?$/i.test(requestUrl(config));
}

/** Best-effort JWT exp check (no signature verification). */
export function isAccessExpired(access) {
  if (!access || typeof access !== 'string') return true;
  const parts = access.split('.');
  if (parts.length !== 3) return false;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    if (!payload || typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 < Date.now() + 5000;
  } catch {
    return false;
  }
}

export function isTransientAuthError(error) {
  if (!error || typeof error !== 'object') return false;
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.message === 'Network Error') {
    return true;
  }
  const status = error.response && error.response.status;
  if (!status) return true;
  return status >= 500;
}

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function readStoredTokens() {
  return {
    access: localStorage.getItem(ACCESS_KEY),
    refresh: localStorage.getItem(REFRESH_KEY),
  };
}

function storeTokens(access, refresh) {
  if (access) localStorage.setItem(ACCESS_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
}

/**
 * Another tab may have rotated the same refresh while this request failed.
 * @param {string} previousRefresh
 */
async function accessFromPeerRotation(previousRefresh) {
  const waits = typeof navigator !== 'undefined' && navigator.locks ? [200] : [200, 400, 700];
  for (let i = 0; i < waits.length; i += 1) {
    await delay(waits[i]);
    const current = readStoredTokens();
    if (
      current.refresh
      && current.refresh !== previousRefresh
      && current.access
      && !isAccessExpired(current.access)
    ) {
      return current;
    }
  }
  return null;
}

/**
 * @param {string | null} failedAccess access token that already received 401
 */
async function performRefresh(failedAccess) {
  const current = readStoredTokens();
  if (current.access && current.access !== failedAccess && !isAccessExpired(current.access)) {
    return { status: 'ok', access: current.access, refresh: current.refresh };
  }
  if (!current.refresh) return { status: 'auth' };

  const sentRefresh = current.refresh;
  try {
    const response = await axios.post(
      refreshUrl(),
      { refresh: sentRefresh },
      { timeout: AUTH_TIMEOUT_MS },
    );
    const data = response.data || {};
    if (!data.access) return { status: 'auth' };
    storeTokens(data.access, data.refresh || null);
    return { status: 'ok', access: data.access, refresh: data.refresh || sentRefresh };
  } catch (error) {
    if (isTransientAuthError(error)) return { status: 'transient' };
    const peer = await accessFromPeerRotation(sentRefresh);
    if (peer && peer.access) {
      return { status: 'ok', access: peer.access, refresh: peer.refresh };
    }
    return { status: 'auth' };
  }
}

function withRefreshLock(fn) {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : null;
  if (locks && typeof locks.request === 'function') {
    return locks.request(REFRESH_LOCK, fn);
  }
  return fn();
}

/**
 * @param {string | null} [failedAccess]
 * @returns {Promise<{ status: 'ok', access: string, refresh: string | null } | { status: 'transient' } | { status: 'auth' }>}
 */
export function refreshSessionTokens(failedAccess = null) {
  if (!refreshPromise) {
    let run;
    run = withRefreshLock(() => performRefresh(failedAccess)).finally(() => {
      if (refreshPromise === run) refreshPromise = null;
    });
    refreshPromise = run;
  }
  return refreshPromise;
}
